import { useCallback, useState } from "react";

import type { FolderDialogState } from "../components/FolderDialog.js";
import { ApiError, api } from "./api.js";
import type { MailFolder } from "./types.js";

/**
 * Mappen aanmaken, hernoemen, nestelen en verwijderen.
 *
 * Zit in een eigen hook zodat Mail.tsx alleen de bedrading houdt. De
 * meldingen komen van de server: die formuleert ze in het Nederlands en kent
 * als enige de delimiter, de bestaande namen en het aantal berichten in een map.
 */

/** De server weigert deze twee tot de gebruiker uitdrukkelijk bevestigt. */
const BEVESTIGING_NODIG = new Set(["folder_has_children", "folder_not_empty"]);

export interface PendingDelete {
  folder: MailFolder;
  /** De weigering van de server, letterlijk; die noemt het echte aantal. */
  message: string;
}

interface Callbacks {
  /** Elke wijziging antwoordt met de verse lijst. */
  onFolders: (folders: MailFolder[]) => void;
  /** Een hernoeming verplaatst de hele subboom; de open map kan meeverhuisd zijn. */
  onRenamed: (previousPath: string, nextPath: string, delimiter: string) => void;
  onRemoved: (removed: string[]) => void;
  onNotice: (text: string) => void;
}

export interface FolderActions {
  busy: boolean;
  dialog: FolderDialogState | null;
  dialogError: string | null;
  pendingDelete: PendingDelete | null;

  openCreate: (parent: MailFolder | null) => void;
  openRename: (folder: MailFolder) => void;
  closeDialog: () => void;
  submitDialog: (name: string) => void;

  remove: (folder: MailFolder) => void;
  confirmRemove: () => void;
  cancelRemove: () => void;

  /** Slepen van map naar map; target null = naar het hoogste niveau. */
  nest: (source: MailFolder, target: MailFolder | null) => void;
}

function messageOf(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : "Er ging iets mis. Probeer het opnieuw.";
}

export function useFolderActions(callbacks: Callbacks): FolderActions {
  const { onFolders, onRenamed, onRemoved, onNotice } = callbacks;

  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<FolderDialogState | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const openCreate = useCallback((parent: MailFolder | null) => {
    setDialogError(null);
    setDialog({ mode: "create", parent, target: null });
  }, []);

  const openRename = useCallback((folder: MailFolder) => {
    setDialogError(null);
    setDialog({ mode: "rename", parent: null, target: folder });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);

  const submitDialog = useCallback(
    (name: string) => {
      if (dialog === null) return;
      setBusy(true);
      setDialogError(null);

      const werk =
        dialog.mode === "create"
          ? api.createFolder(name, dialog.parent?.path ?? null).then((result) => {
              onFolders(result.folders);
              onNotice(`Map "${name}" aangemaakt.`);
            })
          : (() => {
              const target = dialog.target;
              if (target === null) return Promise.resolve();
              return api.renameFolder(target.path, { name }).then((result) => {
                onFolders(result.folders);
                onRenamed(result.previousPath, result.path, target.delimiter);
                onNotice(`Map hernoemd naar "${name}".`);
              });
            })();

      werk
        // De melding blijft in de dialoog staan: de gebruiker moet de naam
        // kunnen aanpassen zonder opnieuw te beginnen.
        .then(() => setDialog(null))
        .catch((cause: unknown) => setDialogError(messageOf(cause)))
        .finally(() => setBusy(false));
    },
    [dialog, onFolders, onNotice, onRenamed],
  );

  const wis = useCallback(
    (folder: MailFolder, force: boolean) => {
      setBusy(true);
      api
        .deleteFolder(folder.path, { recursive: force, confirm: force })
        .then((result) => {
          onFolders(result.folders);
          onRemoved(result.removed);
          onNotice(`Map "${folder.name}" verwijderd.`);
          setPendingDelete(null);
        })
        .catch((cause: unknown) => {
          // Geen fout maar een vraag: de server wil eerst een uitdrukkelijk ja
          // voordat hij post of submappen weggooit.
          if (cause instanceof ApiError && BEVESTIGING_NODIG.has(cause.code)) {
            setPendingDelete({ folder, message: cause.message });
            return;
          }
          setPendingDelete(null);
          onNotice(messageOf(cause));
        })
        .finally(() => setBusy(false));
    },
    [onFolders, onNotice, onRemoved],
  );

  const remove = useCallback(
    (folder: MailFolder) => {
      // Eerst zonder force: de server vertelt dan zelf of er iets in zit, en
      // met hoeveel. Dat getal komt in de bevestiging te staan.
      wis(folder, false);
    },
    [wis],
  );

  const confirmRemove = useCallback(() => {
    if (pendingDelete !== null) wis(pendingDelete.folder, true);
  }, [pendingDelete, wis]);

  const cancelRemove = useCallback(() => setPendingDelete(null), []);

  const nest = useCallback(
    (source: MailFolder, target: MailFolder | null) => {
      setBusy(true);
      api
        .renameFolder(source.path, { parent: target?.path ?? null })
        .then((result) => {
          onFolders(result.folders);
          onRenamed(result.previousPath, result.path, source.delimiter);
          onNotice(
            target === null
              ? `Map "${source.name}" naar het hoogste niveau verplaatst.`
              : `Map "${source.name}" verplaatst naar ${target.name}.`,
          );
        })
        .catch((cause: unknown) => onNotice(messageOf(cause)))
        .finally(() => setBusy(false));
    },
    [onFolders, onNotice, onRenamed],
  );

  return {
    busy,
    dialog,
    dialogError,
    pendingDelete,
    openCreate,
    openRename,
    closeDialog,
    submitDialog,
    remove,
    confirmRemove,
    cancelRemove,
    nest,
  };
}
